import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import AppLayout from "@cloudscape-design/components/app-layout";
import SideNavigation from "@cloudscape-design/components/side-navigation";
import TopNavigation from "@cloudscape-design/components/top-navigation";
import { useTranslation } from "react-i18next";
import { getCurrentUser, signOut, handleOAuthCallback } from "./auth";
import { api } from "./api";
import { getPreference, setPreference } from "./preferences";
import "./i18n";
import LoginPage from "./pages/LoginPage";
import FeedsPage from "./pages/FeedsPage";
import AdminPage from "./pages/AdminPage";
import SourceFeedsPage from "./pages/SourceFeedsPage";
import ServicesPage from "./pages/ServicesPage";

function AppContent({ user, isAdmin, darkMode, setDarkMode, handleLogout, adminViewMode, setAdminViewMode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [toolsOpen, setToolsOpen] = useState(true);
  const [toolsContent, setToolsContent] = useState(null);
  const [navigationOpen, setNavigationOpen] = useState(true);
  const [activeHref, setActiveHref] = useState(location.pathname);

  useEffect(() => {
    setActiveHref(location.pathname);
  }, [location.pathname]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const langLabels = {
    en: "🇺🇸 EN",
    ja: "🇯🇵 JA",
    es: "🇪🇸 ES",
    fr: "🇫🇷 FR",
    pt: "🇧🇷 PT",
    ko: "🇰🇷 KO",
    zh: "🇨🇳 ZH",
  };

  const utilities = [
    {
      type: "menu-dropdown",
      text: langLabels[i18n.language] || i18n.language.toUpperCase(),
      iconName: "globe",
      items: [
        { id: "en", text: "🇺🇸 English" },
        { id: "ja", text: "🇯🇵 日本語" },
        { id: "es", text: "🇪🇸 Español" },
        { id: "fr", text: "🇫🇷 Français" },
        { id: "pt", text: "🇧🇷 Português" },
        { id: "ko", text: "🇰🇷 한국어" },
        { id: "zh", text: "🇨🇳 中文" },
      ],
      onItemClick: ({ detail }) => changeLanguage(detail.id),
    },
    {
      type: "button",
      text: darkMode ? t("nav.menu.light-mode") : t("nav.menu.dark-mode"),
      iconName: darkMode ? "status-positive" : "status-negative",
      onClick: () => setDarkMode(!darkMode),
    },
    {
      type: "button",
      text: user.email,
      iconName: "user-profile",
    },
    {
      type: "button",
      text: t("auth.action.sign-out"),
      onClick: handleLogout,
    },
  ];

  // Add admin view toggle for primary admins
  if (isAdmin) {
    utilities.unshift({
      type: "button",
      text: adminViewMode ? t("nav.menu.user-view") : t("nav.menu.admin-view"),
      iconName: adminViewMode ? "user-profile" : "settings",
      onClick: () => setAdminViewMode(!adminViewMode),
    });
  }

  const navigationItems = [
    { type: "link", text: t("nav.menu.feeds"), href: "/" },
    { type: "link", text: t("nav.menu.services"), href: "/services" },
  ];

  // Only show Admin menu item when in admin view mode
  if (isAdmin && adminViewMode) {
    navigationItems.push({ type: "link", text: t("nav.menu.admin"), href: "/admin" });
    navigationItems.push({ type: "link", text: t("nav.menu.source-feeds"), href: "/source-feeds" });
  }

  const envBanner = process.env.REACT_APP_ENVIRONMENT && process.env.REACT_APP_ENVIRONMENT !== "production";

  return (
    <div className={darkMode ? "awsui-dark-mode" : ""}>
      {envBanner && (
        <div
          style={{
            background: "#ff9900",
            color: "#000",
            textAlign: "center",
            padding: "4px 0",
            fontWeight: "bold",
            fontSize: "13px",
            letterSpacing: "0.5px",
            zIndex: 9999,
            position: "relative",
          }}
        >
          ⚠️ {process.env.REACT_APP_ENVIRONMENT} ENVIRONMENT — This is not production
        </div>
      )}
      <TopNavigation
        identity={{
          href: "/",
          title: t("app.main.title"),
          logo: {
            src: "/logo.png",
            alt: t("app.main.title"),
          },
        }}
        utilities={utilities}
      />
      <AppLayout
        navigation={
          <SideNavigation
            activeHref={activeHref}
            items={navigationItems}
            onFollow={(event) => {
              if (!event.detail.external) {
                event.preventDefault();
                navigate(event.detail.href);
              }
            }}
          />
        }
        navigationWidth={220}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        toolsOpen={toolsOpen}
        onToolsChange={({ detail }) => setToolsOpen(detail.open)}
        tools={toolsContent}
        content={
          <Routes>
            <Route
              path="/"
              element={<FeedsPage setToolsOpen={setToolsOpen} setToolsContent={setToolsContent} isAdmin={isAdmin} adminViewMode={adminViewMode} />}
            />
            <Route
              path="/services"
              element={<ServicesPage setToolsOpen={setToolsOpen} setToolsContent={setToolsContent} isAdmin={isAdmin} adminViewMode={adminViewMode} />}
            />
            {isAdmin && <Route path="/admin" element={<AdminPage setToolsOpen={setToolsOpen} setToolsContent={setToolsContent} />} />}
            {isAdmin && <Route path="/source-feeds" element={<SourceFeedsPage setToolsOpen={setToolsOpen} setToolsContent={setToolsContent} />} />}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        }
      />
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [darkMode, setDarkMode] = useState(() => getPreference("darkMode"));
  const [adminViewMode, setAdminViewMode] = useState(() => getPreference("adminViewMode", true));

  // Persist dark mode preference
  useEffect(() => {
    setPreference("darkMode", darkMode);
  }, [darkMode]);

  // Persist admin view mode preference
  useEffect(() => {
    setPreference("adminViewMode", adminViewMode);
  }, [adminViewMode]);

  useEffect(() => {
    checkAuth();

    // Refresh token every 50 minutes (before 1 hour expiry)
    const refreshInterval = setInterval(
      () => {
        checkAuth();
      },
      50 * 60 * 1000,
    ); // 50 minutes

    return () => clearInterval(refreshInterval);
  }, []);

  const checkAuth = async () => {
    try {
      // First check if this is an OAuth callback
      const oauthUser = await handleOAuthCallback();
      if (oauthUser) {
        setUser(oauthUser);
        api.setToken(oauthUser.token);

        // Check admin status
        try {
          await api.getAdmins();
          setIsAdmin(true);
        } catch {
          setIsAdmin(false);
        }
        setLoading(false);
        return;
      }

      // Otherwise check for existing session
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      api.setToken(currentUser.token);

      // Check if user is admin
      try {
        await api.getAdmins();
        setIsAdmin(true);
      } catch {
        setIsAdmin(false);
      }
    } catch (err) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (userData) => {
    setUser(userData);
    api.setToken(userData.token);

    // Check admin status
    try {
      await api.getAdmins();
      setIsAdmin(true);
    } catch {
      setIsAdmin(false);
    }
  };

  const handleLogout = () => {
    signOut();
    setUser(null);
    setIsAdmin(false);
    api.setToken(null);
  };

  if (loading) {
    return <div>{t("app.main.loading")}</div>;
  }

  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <Router>
      <AppContent
        user={user}
        isAdmin={isAdmin}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        handleLogout={handleLogout}
        adminViewMode={adminViewMode}
        setAdminViewMode={setAdminViewMode}
      />
    </Router>
  );
}

export default App;
